import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { StickyBackBar } from '@/components/layout/StickyBackBar';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { CodeSnippet } from '@/components/ui/CodeSnippet';
import { Dialog } from '@/components/ui/Dialog';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { KnowledgeLink } from '@/components/ui/KnowledgeLink';
import { RadioGroup } from '@/components/ui/RadioGroup';
import { Select } from '@/components/ui/Select';
import { TOGGLE_BUTTON_BASE, TOGGLE_STYLES } from '@/styles/toggleStyles';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { usePageTitle } from '@/hooks/usePageTitle';
import { getErrorMessage } from '@/utils/errorHandler';
import { toast } from 'sonner';
import {
    ArrowTopRightOnSquareIcon,
    CheckIcon,
    ClipboardDocumentIcon,
    CommandLineIcon,
    ExclamationTriangleIcon,
    PlusIcon,
    ShieldCheckIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import {
    useCreatePersonalAccessToken,
    usePersonalAccessTokens,
    useRevokePersonalAccessToken,
} from '@/api/hooks/usePersonalAccessTokens';
import { useConnectedApps, useRevokeConnectedApp } from '@/api/hooks/useConnectedApps';
import { describeOAuthScope } from '@/utils/oauthScopes';
import {
    MCP_CLIENTS,
    findMcpClient,
    mcpServerUrl,
    type ConnectionPath,
    type McpClientId,
    type SnippetContext,
} from '@/utils/mcpClients';
import type {
    ConnectedApp,
    CreatePersonalAccessTokenResponse,
    PersonalAccessToken,
    PersonalAccessTokenScope,
} from '@/api/types';

type ScopeChoice = 'read' | 'read-write';

const SCOPE_OPTIONS: { value: ScopeChoice; label: string; description: string }[] = [
    {
        value: 'read',
        label: 'Read',
        description: 'View ledgers, values and analytics.',
    },
    {
        value: 'read-write',
        label: 'Read + write',
        description:
            'Also add, change and delete values and cash flows on your ledgers. Adding costs credits, as in the app.',
    },
];

const EXPIRY_OPTIONS: { value: number | null; label: string }[] = [
    { value: null, label: 'Never' },
    { value: 30, label: '30 days' },
    { value: 90, label: '90 days' },
    { value: 365, label: '365 days' },
];

const NAME_MAX_LENGTH = 100;

function formatDateTime(value: string): string {
    return new Date(value).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function formatDate(value: string): string {
    return new Date(value).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

function formatScopes(scopes: string[]): string {
    return scopes.includes('mcp:write') ? 'Read + write' : 'Read';
}

function scopesFor(choice: ScopeChoice): PersonalAccessTokenScope[] {
    return choice === 'read-write' ? ['mcp:read', 'mcp:write'] : ['mcp:read'];
}

interface ConnectionPathBlockProps {
    heading: string;
    path: ConnectionPath;
    context: SnippetContext;
    testId: string;
    isRecommended?: boolean;
    /** Rendered inside the first step, for example the "Create a token" button. */
    firstStepAction?: ReactNode;
}

/**
 * Renders one connection path: heading, numbered steps, optional snippet and note.
 */
function ConnectionPathBlock({
    heading,
    path,
    context,
    testId,
    isRecommended = false,
    firstStepAction,
}: ConnectionPathBlockProps): ReactNode {
    const snippet = path.snippet?.(context);

    return (
        <div data-testid={testId} className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-medium text-gray-900 dark:text-white">{heading}</h3>
                {isRecommended && (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                        Recommended
                    </span>
                )}
            </div>

            <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm text-gray-600 dark:text-gray-400">
                {path.steps.map((step, index) => (
                    <li key={step}>
                        <span className="align-middle">{step}</span>
                        {index === 0 && firstStepAction}
                    </li>
                ))}
            </ol>

            {snippet && (
                <CodeSnippet
                    className="mt-3"
                    code={snippet.code}
                    label={snippet.label}
                    language={snippet.language}
                    testId={`${testId}-snippet`}
                />
            )}

            {path.note && (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{path.note}</p>
            )}
        </div>
    );
}

export function ConnectedAppsPage() {
    usePageTitle('Connected apps');

    const { data, isLoading, error } = usePersonalAccessTokens();
    const createToken = useCreatePersonalAccessToken();
    const revokeToken = useRevokePersonalAccessToken();

    const {
        data: connectedAppsData,
        isLoading: isLoadingApps,
        error: appsError,
    } = useConnectedApps();
    const revokeApp = useRevokeConnectedApp();

    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [name, setName] = useState('');
    const [scopeChoice, setScopeChoice] = useState<ScopeChoice>('read');
    const [expiresInDays, setExpiresInDays] = useState<number | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [createdToken, setCreatedToken] = useState<CreatePersonalAccessTokenResponse | null>(null);
    const [selectedClientId, setSelectedClientId] = useState<McpClientId>('claude');
    const [tokenToRevoke, setTokenToRevoke] = useState<PersonalAccessToken | null>(null);
    const [isRevokeOpen, setIsRevokeOpen] = useState(false);
    const [appToRevoke, setAppToRevoke] = useState<ConnectedApp | null>(null);
    const [isAppRevokeOpen, setIsAppRevokeOpen] = useState(false);

    const serverUrlCopy = useCopyToClipboard('Server URL copied to clipboard');
    const tokenCopy = useCopyToClipboard('Token copied to clipboard');

    const tokenPanelRef = useRef<HTMLDivElement>(null);
    const tokenPanelHeadingRef = useRef<HTMLHeadingElement>(null);

    const tokens = data?.items ?? [];
    const connectedApps = connectedAppsData?.items ?? [];

    const serverUrl = mcpServerUrl(window.location.origin);
    const selectedClient = findMcpClient(selectedClientId);
    const snippetContext: SnippetContext = { serverUrl, token: createdToken?.token ?? null };
    const nextStepSnippet = selectedClient.token?.snippet?.(snippetContext);
    const nextStepCopyLabel =
        nextStepSnippet && nextStepSnippet.language !== 'bash'
            ? 'Copy the configuration with your new token'
            : 'Copy the command with your new token';

    // The panel renders at the top of the page, far above the button that creates
    // the token, so bring it into view and hand it the focus.
    useEffect(() => {
        if (!createdToken) {
            return;
        }

        tokenPanelRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
        tokenPanelHeadingRef.current?.focus({ preventScroll: true });
    }, [createdToken]);

    function resetForm() {
        setName('');
        setScopeChoice('read');
        setExpiresInDays(null);
        setFormError(null);
    }

    function openCreateDialog() {
        resetForm();
        setIsCreateOpen(true);
    }

    async function handleCreate(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        const trimmedName = name.trim();
        if (trimmedName.length === 0) {
            setFormError('Enter a name for this token.');
            return;
        }

        try {
            const response = await createToken.mutateAsync({
                name: trimmedName,
                scopes: scopesFor(scopeChoice),
                expiresInDays,
            });

            setCreatedToken(response);
            setIsCreateOpen(false);
            resetForm();
            toast.success('Token created');
        } catch (err) {
            setFormError(getErrorMessage(err));
        }
    }

    async function handleCopy() {
        if (!createdToken) return;

        await tokenCopy.copy(createdToken.token);
    }

    function openRevokeDialog(token: PersonalAccessToken) {
        setTokenToRevoke(token);
        setIsRevokeOpen(true);
    }

    async function handleRevoke() {
        if (!tokenToRevoke) return;

        try {
            await revokeToken.mutateAsync(tokenToRevoke.id);
            toast.success('Token revoked');
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setIsRevokeOpen(false);
        }
    }

    function openAppRevokeDialog(app: ConnectedApp) {
        setAppToRevoke(app);
        setIsAppRevokeOpen(true);
    }

    async function handleRevokeApp() {
        if (!appToRevoke) return;

        try {
            await revokeApp.mutateAsync(appToRevoke.id);
            toast.success('Access revoked');
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setIsAppRevokeOpen(false);
        }
    }

    return (
        <>
            <StickyBackBar destinationLabel="Settings" backPath="/settings" />
            <div className="space-y-6 pt-4">
                <PageHeader title="Connected apps" />

                <p className="text-gray-600 dark:text-gray-400">
                    Use Netfiscus from AI assistants and developer tools. They can read your ledgers
                    and, if you allow it, add values for you.{' '}
                    <KnowledgeLink page="ai-assistants">Learn how it works</KnowledgeLink>
                </p>

                {createdToken && (
                    <div
                        ref={tokenPanelRef}
                        className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20"
                    >
                        <div className="flex items-start gap-3">
                            <ExclamationTriangleIcon
                                className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400"
                                aria-hidden="true"
                            />
                            <div className="min-w-0 flex-1">
                                {/* Only the heading and the one-line summary are announced. The
                                    token itself must never be read aloud. */}
                                <div role="status" aria-live="polite">
                                    <h2
                                        ref={tokenPanelHeadingRef}
                                        tabIndex={-1}
                                        className="font-medium text-amber-900 outline-hidden dark:text-amber-200"
                                    >
                                        Copy your token now
                                    </h2>
                                    <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
                                        This is the only time the token for &quot;
                                        {createdToken.item.name}
                                        &quot; is shown. Copy it now and paste it into your app.
                                    </p>
                                </div>
                                <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                                    <code
                                        data-testid="plaintext-token"
                                        className="min-w-0 flex-1 break-all rounded-md border border-amber-200 bg-white px-3 py-2 font-mono text-sm text-gray-900 dark:border-amber-800 dark:bg-dark dark:text-gray-100"
                                    >
                                        {createdToken.token}
                                    </code>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => void handleCopy()}
                                    >
                                        {tokenCopy.isCopied ? (
                                            <CheckIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                                        ) : (
                                            <ClipboardDocumentIcon
                                                className="mr-2 h-4 w-4"
                                                aria-hidden="true"
                                            />
                                        )}
                                        {tokenCopy.isCopied ? 'Copied' : 'Copy'}
                                    </Button>
                                </div>

                                <div className="mt-4 border-t border-amber-200 pt-3 dark:border-amber-800">
                                    <h3 className="text-sm font-medium text-amber-900 dark:text-amber-200">
                                        Next step
                                    </h3>
                                    {nextStepSnippet ? (
                                        <CodeSnippet
                                            className="mt-2"
                                            code={nextStepSnippet.code}
                                            label={nextStepSnippet.label}
                                            language={nextStepSnippet.language}
                                            ariaLabel={nextStepCopyLabel}
                                            testId="next-step-snippet"
                                        />
                                    ) : (
                                        <p
                                            data-testid="next-step-note"
                                            className="mt-2 text-sm text-amber-800 dark:text-amber-300"
                                        >
                                            {selectedClient.name} signs in instead of using tokens.
                                            Pick another app below to see where this token goes.
                                        </p>
                                    )}
                                </div>

                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="mt-3"
                                    onClick={() => setCreatedToken(null)}
                                >
                                    I saved it
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                <section
                    aria-labelledby="connect-assistant-heading"
                    className="min-w-0 space-y-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-secondary"
                >
                    <h2
                        id="connect-assistant-heading"
                        className="text-lg font-medium text-gray-900 dark:text-white"
                    >
                        Connect an AI assistant
                    </h2>

                    <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Server URL
                        </p>
                        <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center">
                            <code
                                data-testid="mcp-server-url"
                                className="min-w-0 flex-1 break-all rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-900 dark:border-gray-700 dark:bg-dark dark:text-gray-100"
                            >
                                {serverUrl}
                            </code>
                            <Button
                                variant="secondary"
                                size="sm"
                                aria-label="Copy server URL"
                                className="self-start sm:self-auto"
                                onClick={() => void serverUrlCopy.copy(serverUrl)}
                            >
                                {serverUrlCopy.isCopied ? (
                                    <CheckIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                                ) : (
                                    <ClipboardDocumentIcon
                                        className="mr-2 h-4 w-4"
                                        aria-hidden="true"
                                    />
                                )}
                                {serverUrlCopy.isCopied ? 'Copied' : 'Copy'}
                            </Button>
                        </div>
                    </div>

                    <div className="min-w-0">
                        <p
                            id="client-picker-label"
                            className="text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            Which app do you use?
                        </p>
                        <div
                            role="group"
                            aria-labelledby="client-picker-label"
                            className={`${TOGGLE_STYLES.container} mt-2 max-w-full flex-wrap gap-1`}
                        >
                            {MCP_CLIENTS.map((client) => {
                                const isActive = client.id === selectedClientId;

                                return (
                                    <button
                                        key={client.id}
                                        type="button"
                                        aria-pressed={isActive}
                                        onClick={() => setSelectedClientId(client.id)}
                                        className={`px-3 py-1.5 text-sm font-medium ${TOGGLE_BUTTON_BASE} ${
                                            isActive ? TOGGLE_STYLES.active : TOGGLE_STYLES.inactive
                                        }`}
                                    >
                                        {client.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="min-w-0 space-y-5">
                        <p
                            data-testid="selected-client-summary"
                            className="text-sm text-gray-600 dark:text-gray-400"
                        >
                            {selectedClient.name} · {selectedClient.tagline}
                        </p>

                        {selectedClient.signin && (
                            <ConnectionPathBlock
                                heading="Sign in"
                                path={selectedClient.signin}
                                context={snippetContext}
                                testId="signin-path"
                                isRecommended={selectedClient.token !== undefined}
                            />
                        )}

                        {selectedClient.token && (
                            <ConnectionPathBlock
                                heading="Use a token instead"
                                path={selectedClient.token}
                                context={snippetContext}
                                testId="token-path"
                                firstStepAction={
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        className="ml-2 align-middle"
                                        onClick={openCreateDialog}
                                    >
                                        Create a token
                                    </Button>
                                }
                            />
                        )}

                        <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
                            {selectedClient.vendorDocsUrl && (
                                <a
                                    href={selectedClient.vendorDocsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-accent hover:underline"
                                >
                                    Official {selectedClient.name} guide
                                    <ArrowTopRightOnSquareIcon
                                        className="h-3.5 w-3.5"
                                        aria-hidden="true"
                                    />
                                </a>
                            )}
                            <KnowledgeLink page="ai-assistants" anchor={selectedClient.id}>
                                Step-by-step guide
                            </KnowledgeLink>
                        </div>
                    </div>
                </section>

                <section aria-labelledby="authorized-apps-heading" className="space-y-3 pt-2">
                    <h2
                        id="authorized-apps-heading"
                        className="text-lg font-medium text-gray-900 dark:text-white"
                    >
                        Authorized apps
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Apps that signed in with your Netfiscus account. Revoking an app ends its
                        access at once.
                    </p>

                    {isLoadingApps && (
                        <div className="flex justify-center py-8">
                            <div className="text-gray-500 dark:text-gray-400">Loading apps...</div>
                        </div>
                    )}

                    {appsError && <ErrorAlert message={getErrorMessage(appsError)} />}

                    {!isLoadingApps && !appsError && connectedApps.length === 0 && (
                        <div className="rounded-lg border border-gray-200 bg-white p-6 text-center dark:border-gray-700 dark:bg-secondary">
                            <ShieldCheckIcon
                                className="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500"
                                aria-hidden="true"
                            />
                            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                                Nothing here yet. When an app signs in and you approve it, it
                                appears here.
                            </p>
                        </div>
                    )}

                    {!isLoadingApps && !appsError && connectedApps.length > 0 && (
                        <ul className="space-y-3">
                            {connectedApps.map((app) => (
                                <li
                                    key={app.id}
                                    data-testid={`connected-app-${app.id}`}
                                    className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-secondary sm:flex-row sm:items-center sm:justify-between"
                                >
                                    <div className="min-w-0">
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {app.clientName}
                                        </p>
                                        <ul className="mt-1 space-y-0.5 text-sm text-gray-500 dark:text-gray-400">
                                            {app.scopes.map((scope) => (
                                                <li key={scope}>{describeOAuthScope(scope)}</li>
                                            ))}
                                        </ul>
                                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                            Authorized {formatDateTime(app.grantedAt)}
                                        </p>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        aria-label={`Revoke access for ${app.clientName}`}
                                        onClick={() => openAppRevokeDialog(app)}
                                        className="self-start text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300 sm:self-auto"
                                    >
                                        <TrashIcon className="h-4 w-4" aria-hidden="true" />
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section aria-labelledby="tokens-heading" className="space-y-3 pt-2">
                    <div className="flex min-h-[40px] flex-wrap items-center justify-between gap-3">
                        <h2
                            id="tokens-heading"
                            className="text-lg font-medium text-gray-900 dark:text-white"
                        >
                            Personal access tokens
                        </h2>
                        <Button size="sm" onClick={openCreateDialog}>
                            <PlusIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                            New token
                        </Button>
                    </div>

                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Tokens are for tools that cannot sign in on their own: terminal tools,
                        scripts and custom integrations. Treat a token like a password.
                    </p>

                    {isLoading && (
                        <div className="flex justify-center py-12">
                            <div className="text-gray-500 dark:text-gray-400">
                                Loading tokens...
                            </div>
                        </div>
                    )}

                    {error && <ErrorAlert message={getErrorMessage(error)} />}

                    {!isLoading && !error && tokens.length === 0 && (
                        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center dark:border-gray-700 dark:bg-secondary">
                            <CommandLineIcon
                                className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
                                aria-hidden="true"
                            />
                            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                                No tokens yet
                            </h3>
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                Most apps sign in instead. Create a token only if your tool asks for
                                one.
                            </p>
                            <Button onClick={openCreateDialog} className="mt-4">
                                <PlusIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                                Create your first token
                            </Button>
                        </div>
                    )}

                    {!isLoading && !error && tokens.length > 0 && (
                        <ul className="space-y-3">
                            {tokens.map((token) => (
                                <li
                                    key={token.id}
                                    data-testid={`token-${token.id}`}
                                    className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-secondary sm:flex-row sm:items-center sm:justify-between"
                                >
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <p className="font-medium text-gray-900 dark:text-white">
                                                {token.name}
                                            </p>
                                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-dark dark:text-gray-300">
                                                {formatScopes(token.scopes)}
                                            </span>
                                        </div>
                                        <p className="mt-1 font-mono text-sm text-gray-500 dark:text-gray-400">
                                            {token.tokenPrefix}…
                                        </p>
                                        <div className="mt-1 flex flex-col gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400 sm:flex-row sm:flex-wrap">
                                            <span>Created {formatDateTime(token.createdAt)}</span>
                                            <span>
                                                {token.lastUsedAt
                                                    ? `Last used ${formatDateTime(token.lastUsedAt)}`
                                                    : 'Never used'}
                                            </span>
                                            <span>
                                                {token.expiresAt
                                                    ? `Expires ${formatDate(token.expiresAt)}`
                                                    : 'Never expires'}
                                            </span>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        aria-label={`Revoke ${token.name}`}
                                        onClick={() => openRevokeDialog(token)}
                                        className="self-start text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300 sm:self-auto"
                                    >
                                        <TrashIcon className="h-4 w-4" aria-hidden="true" />
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>

            <Dialog
                open={isCreateOpen}
                onClose={() => setIsCreateOpen(false)}
                title="New personal access token"
                size="lg"
            >
                <form onSubmit={handleCreate} className="space-y-4" noValidate>
                    <div>
                        <label
                            htmlFor="token-name"
                            className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            Name
                        </label>
                        <Input
                            id="token-name"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            maxLength={NAME_MAX_LENGTH}
                            placeholder={selectedClient.tokenNameSuggestion}
                            helperText="Use the name of the app that will hold this token."
                            autoComplete="off"
                        />
                    </div>

                    <RadioGroup
                        label="Access"
                        value={scopeChoice}
                        onChange={setScopeChoice}
                        options={SCOPE_OPTIONS}
                        inline
                    />

                    <Select
                        label="Expiry"
                        value={expiresInDays}
                        onChange={setExpiresInDays}
                        options={EXPIRY_OPTIONS}
                        helperText="Choose when the token stops working. You can revoke it here at any time."
                    />

                    {formError && <ErrorAlert message={formError} />}

                    <div className="flex justify-end gap-3">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setIsCreateOpen(false)}
                            disabled={createToken.isPending}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" isLoading={createToken.isPending}>
                            Create token
                        </Button>
                    </div>
                </form>
            </Dialog>

            <DeleteConfirmDialog
                open={isRevokeOpen && tokenToRevoke !== null}
                onClose={() => setIsRevokeOpen(false)}
                onConfirm={handleRevoke}
                title="Revoke token"
                description={`Are you sure you want to revoke "${tokenToRevoke?.name ?? 'this token'}"? Any app using it loses access immediately.`}
                confirmLabel="Revoke"
                isLoading={revokeToken.isPending}
            />

            <DeleteConfirmDialog
                open={isAppRevokeOpen && appToRevoke !== null}
                onClose={() => setIsAppRevokeOpen(false)}
                onConfirm={handleRevokeApp}
                title="Revoke access"
                description={`Are you sure you want to revoke access for "${appToRevoke?.clientName ?? 'this app'}"? It loses access immediately and must ask for your approval again.`}
                confirmLabel="Revoke"
                isLoading={revokeApp.isPending}
            />
        </>
    );
}
